'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import ScrollReveal from '@/components/ScrollReveal';
import ContactForm from '@/components/ContactForm';
import { useChat } from '../../lib/hooks/useChat';
import toast from 'react-hot-toast';

// ─── DATA ────────────────────────────────────────────────────────────────────

const SERVICES = [
{
  title: 'Prime',
  image: 'https://media.base44.com/images/public/6a59f2868433765f69dbba18/017d9fbaa_generated_image.png/v1/fill/w_281,h_375,al_c,q_90,usm_0.66_1.00_0.01,enc_webp,quality_auto/017d9fbaa_generated_image.webp',
  alt: 'Luxury prime property interior with elegant furnishings',
  desc: 'Exclusive access to London\'s finest prime and super-prime residences.'
},
{
  title: 'Acquisitions',
  image: 'https://media.base44.com/images/public/6a59f2868433765f69dbba18/f840b5412_generated_image.png/v1/fill/w_281,h_375,al_c,q_90,usm_0.66_1.00_0.01,enc_webp,quality_auto/f840b5412_generated_image.webp',
  alt: 'Sophisticated property acquisition consultation setting',
  desc: 'Strategic acquisition advisory for discerning private clients.'
},
{
  title: 'Concierge',
  image: 'https://media.base44.com/images/public/6a59f2868433765f69dbba18/4950336a3_generated_image.png/v1/fill/w_281,h_375,al_c,q_90,usm_0.66_1.00_0.01,enc_webp,quality_auto/4950336a3_generated_image.webp',
  alt: 'Premium concierge service for luxury property clients',
  desc: 'White-glove property management and lifestyle services.'
},
{
  title: 'Storytelling',
  image: 'https://media.base44.com/images/public/6a59f2868433765f69dbba18/18389942d_generated_image.png/v1/fill/w_281,h_375,al_c,q_90,usm_0.66_1.00_0.01,enc_webp,quality_auto/18389942d_generated_image.webp',
  alt: 'Cinematic property storytelling and photography session',
  desc: 'Cinematic marketing that elevates every exceptional home.'
}];


const PROPERTIES = [
{
  slug: 'stockwell-house',
  index: '01',
  title: 'Stockwell House',
  location: 'Stockwell',
  description: 'A beautifully restored Victorian terraced house combining period grandeur with a light, contemporary interior.',
  beds: '4 bed',
  baths: '3 bath',
  size: '280 sqm',
  price: '£3,950,000',
  tag: null,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fdf4c043-1777147353551.png",
  alt: 'Beautifully restored Victorian terraced house in Stockwell with contemporary interior'
},
{
  slug: '49-albert-court',
  index: '02',
  title: '49 Albert Court',
  location: 'South Kensington',
  description: 'An extraordinary duplex apartment of palatial proportions set within the iconic red-brick Albert Court.',
  beds: '4 bed',
  baths: '4 bath',
  size: '520 sqm',
  price: 'POA',
  tag: 'Off Market',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d27b127-1785838847850.png",
  alt: 'Grand duplex apartment in Albert Court South Kensington with palatial proportions'
},
{
  slug: 'alexander-place',
  index: '03',
  title: 'Alexander Place',
  location: 'Kensington',
  description: 'A meticulously restored Victorian townhouse blending period grandeur with contemporary refinement.',
  beds: '4 bed',
  baths: '3 bath',
  size: '4,200 sqft',
  price: '£8,950,000',
  tag: null,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9a7dbf1-1780486222949.png",
  alt: 'Meticulously restored Victorian townhouse in Kensington with period grandeur'
},
{
  slug: 'victoria-road',
  index: '04',
  title: 'Victoria Road',
  location: 'Kensington',
  description: 'An architectural masterpiece offering voluminous reception spaces and a private cinema.',
  beds: '5 bed',
  baths: '4 bath',
  size: '',
  price: '£12,500,000',
  tag: 'Off Market',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_186d19398-1785838849413.png",
  alt: 'Architectural masterpiece on Victoria Road Kensington with private cinema'
},
{
  slug: 'abingdon-road',
  index: '05',
  title: 'Abingdon Road',
  location: 'Notting Hill',
  description: 'A discreetly positioned contemporary residence with landscaped gardens and a spectacular rooftop terrace.',
  beds: '4 bed',
  baths: '3 bath',
  size: '',
  price: '£6,750,000',
  tag: null,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1243af369-1785838849877.png",
  alt: 'Contemporary residence on Abingdon Road Notting Hill with rooftop terrace'
},
{
  slug: 'denbigh-road',
  index: '06',
  title: 'Denbigh Road',
  location: 'Notting Hill',
  description: 'A beautifully proportioned stucco villa, sensitively modernised to create a calm, light-filled family home.',
  beds: '3 bed',
  baths: '2 bath',
  size: '',
  price: '£5,950,000',
  tag: null,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11347cd20-1785838849015.png",
  alt: 'Beautifully proportioned stucco villa on Denbigh Road Notting Hill'
}];


const TEAM = [
{
  slug: 'sian-louise-tangney',
  name: 'Sian-Louise Tangney',
  role: 'Founder & CEO',
  image: '/assets/images/image-1785837208201.png',
  alt: 'Sian-Louise Tangney, Founder and CEO of Key Privé'
},
{
  slug: 'maxie-jameson',
  name: 'Maxie Jameson',
  role: 'Property Consultant',
  image: '/assets/images/IMG_9511-1785837225164.jpeg',
  alt: 'Maxie Jameson, Property Consultant at Key Privé'
}];


// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────

function ProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? scrollTop / docHeight : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 h-[2px] z-[60]" style={{ background: 'rgba(196, 147, 58, 0.1)' }}>
      <div
        className="h-full origin-left transition-transform duration-100"
        style={{
          transform: `scaleX(${progress})`,
          background: 'linear-gradient(90deg, #A67A28, #C4933A, #D4A84B)'
        }} />
      
    </div>);

}

// ─── HERO SECTION ─────────────────────────────────────────────────────────────

function HeroSection() {
  const [searchValue, setSearchValue] = useState('');
  const [mounted, setMounted] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const chatResult = useChat(
    'PERPLEXITY',
    'perplexity/sonar-pro',
    true
  );
  const response = chatResult?.response ?? '';
  const fullResponse = chatResult?.fullResponse ?? null;
  const isLoading = chatResult?.isLoading ?? false;
  const error = chatResult?.error ?? null;
  const sendMessage = chatResult?.sendMessage ?? (async () => {});

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.getElementById(href.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchValue.trim() || isLoading) return;
    setHasSearched(true);
    sendMessage([
    {
      role: 'system',
      content: `You are an expert advisor for Key Privé, a modern private property office in London specialising in prime and super-prime residential property. Answer questions about Key Privé's properties, services (Prime, Acquisitions, Concierge, Storytelling), team, and the London luxury property market. Be concise, authoritative, and elegant in tone. Always cite sources where relevant.`
    },
    { role: 'user', content: searchValue }],
    {
      temperature: 0.5,
      max_tokens: 600,
      web_search_options: { search_context_size: 'medium' }
    });
  };

  // Extract citations from fullResponse
  const citations: Array<{url: string;title?: string;}> = (() => {
    if (!fullResponse || isLoading) return [];
    const fr = fullResponse as Record<string, unknown>;
    const searchResults = fr.search_results as Array<{url: string;title?: string;}> | undefined;
    if (searchResults?.length) return searchResults.slice(0, 4);
    const rawCitations = fr.citations as string[] | undefined;
    if (rawCitations?.length) return rawCitations.slice(0, 4).map((url) => ({ url }));
    return [];
  })();

  return (
    <section id="top" className="relative min-h-screen w-full flex flex-col items-center justify-center px-6 py-32 text-center overflow-hidden"
    style={{ background: '#1A0E08' }}>

      {/* Background image — Ken Burns */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="/assets/images/WhatsApp_Image_2026-07-21_at_17.21.28__1_-1785828577180.jpeg"
          alt="Key Privé door hanger on a luxury property entrance"
          className="w-full h-full object-cover object-center animate-ken-burns"
          style={{ opacity: 0.55 }} />
        
      </div>

      {/* Layered overlays */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(26,14,8,0.6) 0%, rgba(26,14,8,0.2) 40%, rgba(26,14,8,0.85) 100%)' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(26,14,8,0.4) 0%, transparent 50%, rgba(26,14,8,0.4) 100%)' }} />

      {/* KP watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <span className="font-display leading-none" style={{ fontSize: 'clamp(200px, 40vw, 600px)', fontWeight: 300, color: 'rgba(196, 147, 58, 0.05)', lineHeight: 1 }}>
          KP
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">

        {/* KP Logo badge */}
        <div
          className="mb-8"
          style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'scale(1)' : 'scale(0.8)', transition: 'opacity 1s ease 0.2s, transform 1s cubic-bezier(0.16,1,0.3,1) 0.2s' }}>
          
          <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-[#C4933A]/40 shadow-[0_0_40px_rgba(196,147,58,0.2)]">
            <img
              src="/assets/images/WhatsApp_Image_2026-07-21_at_17.21.28__2_-1785832245799.jpeg"
              alt="Key Privé KP monogram logo on rose background"
              className="w-full h-full object-cover" />
            
          </div>
        </div>

        <p
          className="font-body text-xs tracking-[0.35em] uppercase mb-4"
          style={{ color: '#C4933A', opacity: mounted ? 1 : 0, transition: 'opacity 1s ease 0.4s' }}>
          
          A Modern Private Property Office
        </p>

        <h1
          className="font-display text-5xl md:text-7xl lg:text-8xl font-light leading-[1.05] mb-4"
          style={{ color: '#F2EDE6', opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 1s ease 0.5s, transform 1s cubic-bezier(0.16,1,0.3,1) 0.5s' }}>
          
          Key Privé
        </h1>

        {/* Gold divider */}
        <div
          className="mb-5 h-px w-24"
          style={{ background: 'linear-gradient(90deg, transparent, #C4933A, #D4A84B, #C4933A, transparent)', opacity: mounted ? 1 : 0, transition: 'opacity 1s ease 0.8s' }} />
        

        <p
          className="font-body text-sm text-[#F2EDE6]/60 max-w-md mb-10 leading-relaxed tracking-wide"
          style={{ opacity: mounted ? 1 : 0, transition: 'opacity 1s ease 0.9s' }}>
          
          Discretion, creativity and trusted advice for exceptional clients and remarkable homes.
        </p>

        {/* AI Search bar */}
        <div
          className="w-full mb-8"
          style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 1s ease 1s, transform 1s cubic-bezier(0.16,1,0.3,1) 1s' }}>
          
          <form onSubmit={handleSearch} className="relative flex items-center bg-[#F2EDE6]/95 rounded-sm shadow-[0_8px_32px_rgba(26,14,8,0.4)]">
            <div className="pl-4 pr-2" style={{ color: '#C4933A' }}>
              {isLoading ?
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg> :

              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                </svg>
              }
            </div>
            <input
              type="text"
              placeholder="Ask about properties, services, or the market…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 bg-transparent text-[#3C1B0D] font-body text-sm py-4 pr-3 outline-none placeholder:text-[#8A7A70]/60" />
            
            <button
              type="submit"
              disabled={isLoading || !searchValue.trim()}
              className="m-1.5 px-6 py-3 font-body text-[10px] tracking-[0.2em] uppercase transition-all duration-300 hover:shadow-[0_4px_16px_rgba(196,147,58,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #A67A28, #C4933A, #D4A84B)', color: '#1A0E08' }}>
              
              {isLoading ? 'Searching…' : 'Ask AI'}
            </button>
          </form>

          {/* AI Answer panel */}
          {hasSearched && (response || isLoading) &&
          <div
            className="mt-3 rounded-sm text-left overflow-hidden"
            style={{ background: 'rgba(26,14,8,0.92)', border: '1px solid rgba(196,147,58,0.2)', backdropFilter: 'blur(12px)' }}>
              
              {/* Header */}
              <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(196,147,58,0.1)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C4933A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                </svg>
                <span className="font-body text-[9px] tracking-[0.3em] uppercase" style={{ color: '#C4933A' }}>
                  AI Answer · Powered by Perplexity
                </span>
                {!isLoading &&
              <button
                onClick={() => {setHasSearched(false);setSearchValue('');}}
                className="ml-auto font-body text-[9px] tracking-[0.2em] uppercase transition-colors"
                style={{ color: 'rgba(242,237,230,0.3)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(242,237,230,0.7)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(242,237,230,0.3)'}>
                    Clear
                  </button>
              }
              </div>

              {/* Response text */}
              <div className="px-4 py-4">
                {isLoading && !response ?
              <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) =>
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: '#C4933A', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  )}
                    </div>
                    <span className="font-body text-xs" style={{ color: 'rgba(242,237,230,0.4)' }}>Searching the web…</span>
                  </div> :

              <p className="font-body text-sm leading-relaxed" style={{ color: 'rgba(242,237,230,0.85)' }}>
                    {response}
                    {isLoading && <span className="inline-block w-0.5 h-4 ml-0.5 align-middle animate-pulse" style={{ background: '#C4933A' }} />}
                  </p>
              }
              </div>

              {/* Citations */}
              {!isLoading && citations.length > 0 &&
            <div className="px-4 pb-4">
                  <p className="font-body text-[9px] tracking-[0.25em] uppercase mb-2" style={{ color: 'rgba(196,147,58,0.6)' }}>Sources</p>
                  <div className="flex flex-wrap gap-2">
                    {citations.map((cite, i) =>
                <a
                  key={i}
                  href={cite.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-[10px] px-2.5 py-1 rounded-sm transition-all duration-200"
                  style={{ background: 'rgba(196,147,58,0.1)', color: 'rgba(196,147,58,0.8)', border: '1px solid rgba(196,147,58,0.15)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(196,147,58,0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(196,147,58,0.1)'}>
                        [{i + 1}] {cite.title ? cite.title.slice(0, 30) + (cite.title.length > 30 ? '…' : '') : new URL(cite.url).hostname}
                      </a>
                )}
                  </div>
                </div>
            }
            </div>
          }
        </div>

        {/* Nav links */}
        <div
          className="flex items-center flex-wrap justify-center gap-6 md:gap-10"
          style={{ opacity: mounted ? 1 : 0, transition: 'opacity 1s ease 1.1s' }}>
          
          {[
          { label: 'Properties', href: '#portfolio' },
          { label: 'Advisors', href: '#team' },
          { label: 'Services', href: '#services' },
          { label: 'Contact', href: '#contact' }].
          map((link) =>
          <a
            key={link.label}
            href={link.href}
            onClick={(e) => handleNavClick(e, link.href)}
            className="kp-nav-link font-body text-xs tracking-[0.25em] uppercase text-[#F2EDE6]/60 hover:text-[#D4A84B] transition-colors">
            
              {link.label}
            </a>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="font-body text-[9px] tracking-[0.4em] uppercase" style={{ color: 'rgba(196,147,58,0.5)' }}>Scroll</span>
        <div className="kp-scroll-indicator__line" />
      </div>

      {/* Stats bar */}
      <div className="kp-stats-bar">
        {[
        { value: '£2B+', label: 'Transactions' },
        { value: '10+', label: 'Years Experience' },
        { value: '200+', label: 'Clients Served' },
        { value: '100%', label: 'Discretion' }].
        map((stat) =>
        <div key={stat.label} className="text-center">
            <div className="kp-stat__value">{stat.value}</div>
            <div className="kp-stat__label">{stat.label}</div>
          </div>
        )}
      </div>
    </section>);

}

// ─── PHILOSOPHY SECTION ───────────────────────────────────────────────────────

function PhilosophySection() {
  return (
    <section id="philosophy" className="py-24 md:py-36 overflow-hidden" style={{ background: 'linear-gradient(180deg, #1A0E08 0%, #221208 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* Image */}
          <div className="md:col-span-7">
            <ScrollReveal>
              <div className="relative overflow-hidden aspect-[4/3] group">
                <img
                  src="/assets/images/WhatsApp_Image_2026-07-21_at_17.21.28-1785832245808.jpeg"
                  alt="Luxury property entrance with warm golden light and architectural columns"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                
                {/* Gold overlay on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{ background: 'linear-gradient(135deg, rgba(196,147,58,0.1), transparent)' }} />
                {/* Corner accent */}
                <div className="absolute bottom-0 left-0 w-16 h-16 pointer-events-none">
                  <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: 'linear-gradient(90deg, #C4933A, transparent)' }} />
                  <div className="absolute bottom-0 left-0 h-full w-px" style={{ background: 'linear-gradient(0deg, #C4933A, transparent)' }} />
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Text */}
          <div className="md:col-span-5">
            <ScrollReveal delay={150}>
              <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-6" style={{ color: '#C4933A' }}>
                01 — Philosophy
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-light leading-tight mb-6" style={{ color: '#F2EDE6' }}>
                Luxury property has evolved. We represent people first.
              </h2>
              <div className="kp-gold-divider mb-6" />
              <p className="font-body text-sm leading-relaxed mb-6" style={{ color: 'rgba(242,237,230,0.6)' }}>
                Discretion, creativity and trusted advice — not generic brochures and transactional relationships.
              </p>
              <p className="font-body text-sm leading-relaxed" style={{ color: 'rgba(242,237,230,0.45)' }}>
                We believe every exceptional home deserves an exceptional story, told with care and precision.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>);

}

// ─── SERVICES SECTION ─────────────────────────────────────────────────────────

function ServicesSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section id="services" className="py-24 md:py-36" style={{ background: '#1A0E08' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ScrollReveal>
          <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-4" style={{ color: '#C4933A' }}>
            02 — Expertise
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light mb-4" style={{ color: '#F2EDE6' }}>
            <Link href="/disciplines" className="hover:text-[#D4A84B] transition-colors duration-300">
              One private office. Four disciplines.
            </Link>
          </h2>
          <div className="kp-gold-divider mb-6" />
          <p className="font-body text-sm max-w-2xl mb-16 leading-relaxed" style={{ color: 'rgba(242,237,230,0.5)' }}>
            Our modern, tailor-made approach to real estate is based on three fundamentals: the relationships we have, the advanced technology we use, and the high-end production values we apply.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {SERVICES.map((service, i) =>
          <ScrollReveal key={service.title} delay={i * 100}>
              <div
              className="kp-service-card group relative aspect-[3/4] overflow-hidden cursor-pointer"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}>
              
                <img
                src={service.image}
                alt={service.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-108"
                style={{ transform: hoveredIndex === i ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.8s cubic-bezier(0.25,0.46,0.45,0.94)' }} />
              
                {/* Gradient overlay */}
                <div className="absolute inset-0 transition-opacity duration-500"
              style={{ background: 'linear-gradient(to top, rgba(26,14,8,0.9) 0%, rgba(26,14,8,0.3) 50%, transparent 100%)', opacity: hoveredIndex === i ? 1 : 0.7 }} />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-between p-5">
                  <div>
                    <h3 className="font-body text-lg font-medium text-[#F2EDE6]">{service.title}</h3>
                    <div className="h-px w-8 mt-2 transition-all duration-500"
                  style={{ background: 'linear-gradient(90deg, #C4933A, #D4A84B)', width: hoveredIndex === i ? '48px' : '32px' }} />
                  </div>
                  <p
                  className="font-body text-xs leading-relaxed text-[#F2EDE6]/70 transition-all duration-500"
                  style={{ opacity: hoveredIndex === i ? 1 : 0, transform: hoveredIndex === i ? 'translateY(0)' : 'translateY(10px)' }}>
                  
                    {service.desc}
                  </p>
                </div>

                {/* Gold bottom border on hover */}
                <div className="absolute bottom-0 left-0 right-0 h-px transition-transform duration-500 origin-left"
              style={{ background: 'linear-gradient(90deg, #C4933A, #D4A84B)', transform: hoveredIndex === i ? 'scaleX(1)' : 'scaleX(0)' }} />
              </div>
            </ScrollReveal>
          )}
        </div>
      </div>
    </section>);

}

// ─── PORTFOLIO SECTION (DDRE-style accordion expanding cards) ─────────────────

// Filter option types
type PriceFilter = 'all' | 'under5m' | '5m-10m' | 'over10m';
type BedsFilter = 'all' | '3' | '4' | '5+';
type LocationFilter = 'all' | 'london' | 'home-counties';

const LONDON_AREAS = ['Stockwell', 'South Kensington', 'Kensington', 'Notting Hill', 'Chelsea', 'Mayfair', 'Belgravia', 'Knightsbridge', 'Westminster', 'City', 'Canary Wharf', 'Shoreditch', 'Islington', 'Hampstead', 'Richmond', 'Wimbledon'];
const HOME_COUNTIES_AREAS = ['Surrey', 'Kent', 'Essex', 'Hertfordshire', 'Berkshire', 'Buckinghamshire', 'Oxfordshire', 'Hampshire'];

function parsePrice(price: string): number | null {
  if (price === 'POA') return null;
  const cleaned = price.replace(/[£,]/g, '').trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

function filterProperties(
  properties: typeof PROPERTIES,
  price: PriceFilter,
  beds: BedsFilter,
  location: LocationFilter
) {
  return properties.filter((p) => {
    // Price filter
    if (price !== 'all') {
      const val = parsePrice(p.price);
      if (val === null) return false; // POA excluded from price filters
      if (price === 'under5m' && val >= 5000000) return false;
      if (price === '5m-10m' && (val < 5000000 || val > 10000000)) return false;
      if (price === 'over10m' && val <= 10000000) return false;
    }
    // Beds filter
    if (beds !== 'all') {
      const bedNum = parseInt(p.beds);
      if (beds === '3' && bedNum !== 3) return false;
      if (beds === '4' && bedNum !== 4) return false;
      if (beds === '5+' && bedNum < 5) return false;
    }
    // Location filter
    if (location !== 'all') {
      const isLondon = LONDON_AREAS.some((area) => p.location.toLowerCase().includes(area.toLowerCase()));
      const isHomeCounties = HOME_COUNTIES_AREAS.some((area) => p.location.toLowerCase().includes(area.toLowerCase()));
      if (location === 'london' && !isLondon) return false;
      if (location === 'home-counties' && !isHomeCounties) return false;
    }
    return true;
  });
}

function PortfolioSection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('all');
  const [bedsFilter, setBedsFilter] = useState<BedsFilter>('all');
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('all');

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Reset active index when filters change
  useEffect(() => {
    setActiveIndex(null);
  }, [priceFilter, bedsFilter, locationFilter]);

  const filteredProperties = filterProperties(PROPERTIES, priceFilter, bedsFilter, locationFilter);

  const priceOptions: { label: string; value: PriceFilter }[] = [
    { label: 'All Prices', value: 'all' },
    { label: 'Under £5M', value: 'under5m' },
    { label: '£5M – £10M', value: '5m-10m' },
    { label: 'Over £10M', value: 'over10m' },
  ];
  const bedsOptions: { label: string; value: BedsFilter }[] = [
    { label: 'Any Beds', value: 'all' },
    { label: '3 Bed', value: '3' },
    { label: '4 Bed', value: '4' },
    { label: '5+ Bed', value: '5+' },
  ];
  const locationOptions: { label: string; value: LocationFilter }[] = [
    { label: 'All Locations', value: 'all' },
    { label: 'London', value: 'london' },
    { label: 'Home Counties', value: 'home-counties' },
  ];

  const activeFilterCount = (priceFilter !== 'all' ? 1 : 0) + (bedsFilter !== 'all' ? 1 : 0) + (locationFilter !== 'all' ? 1 : 0);

  return (
    <section id="portfolio" style={{ background: '#f1f2f7' }}>
      {/* Section header */}
      <div className="px-6 md:px-10 pt-16 pb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: '#C4933A' }}>
            03 — Portfolio
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light" style={{ color: '#111827' }}>
            Selected Properties.
          </h2>
        </div>
        <Link
          href="/properties/stockwell-house"
          className="font-body text-sm tracking-[0.15em] uppercase pb-1 transition-colors duration-300 hover:opacity-70"
          style={{ color: '#111827', borderBottom: '1px solid #111827' }}>
          View All Properties
        </Link>
      </div>

      {/* ── Filter Tabs ── */}
      <div className="px-6 md:px-10 pb-6 flex flex-col gap-4">
        {/* Price Range */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-body text-[9px] tracking-[0.3em] uppercase w-20 shrink-0" style={{ color: 'rgba(17,24,39,0.4)' }}>
            Price
          </span>
          <div className="flex flex-wrap gap-1.5">
            {priceOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPriceFilter(opt.value)}
                className="font-body text-[10px] tracking-[0.15em] uppercase px-3.5 py-1.5 transition-all duration-200"
                style={{
                  background: priceFilter === opt.value ? 'linear-gradient(135deg, #A67A28, #C4933A)' : 'rgba(17,24,39,0.06)',
                  color: priceFilter === opt.value ? '#1A0E08' : 'rgba(17,24,39,0.55)',
                  border: priceFilter === opt.value ? '1px solid transparent' : '1px solid rgba(17,24,39,0.1)',
                  borderRadius: '2px',
                  fontWeight: priceFilter === opt.value ? 500 : 400,
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Beds */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-body text-[9px] tracking-[0.3em] uppercase w-20 shrink-0" style={{ color: 'rgba(17,24,39,0.4)' }}>
            Beds
          </span>
          <div className="flex flex-wrap gap-1.5">
            {bedsOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setBedsFilter(opt.value)}
                className="font-body text-[10px] tracking-[0.15em] uppercase px-3.5 py-1.5 transition-all duration-200"
                style={{
                  background: bedsFilter === opt.value ? 'linear-gradient(135deg, #A67A28, #C4933A)' : 'rgba(17,24,39,0.06)',
                  color: bedsFilter === opt.value ? '#1A0E08' : 'rgba(17,24,39,0.55)',
                  border: bedsFilter === opt.value ? '1px solid transparent' : '1px solid rgba(17,24,39,0.1)',
                  borderRadius: '2px',
                  fontWeight: bedsFilter === opt.value ? 500 : 400,
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Location */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-body text-[9px] tracking-[0.3em] uppercase w-20 shrink-0" style={{ color: 'rgba(17,24,39,0.4)' }}>
            Location
          </span>
          <div className="flex flex-wrap gap-1.5">
            {locationOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setLocationFilter(opt.value)}
                className="font-body text-[10px] tracking-[0.15em] uppercase px-3.5 py-1.5 transition-all duration-200"
                style={{
                  background: locationFilter === opt.value ? 'linear-gradient(135deg, #A67A28, #C4933A)' : 'rgba(17,24,39,0.06)',
                  color: locationFilter === opt.value ? '#1A0E08' : 'rgba(17,24,39,0.55)',
                  border: locationFilter === opt.value ? '1px solid transparent' : '1px solid rgba(17,24,39,0.1)',
                  borderRadius: '2px',
                  fontWeight: locationFilter === opt.value ? 500 : 400,
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active filter summary + clear */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-3 pt-1">
            <span className="font-body text-[10px] tracking-[0.15em]" style={{ color: 'rgba(17,24,39,0.45)' }}>
              {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'} found
            </span>
            <button
              onClick={() => { setPriceFilter('all'); setBedsFilter('all'); setLocationFilter('all'); }}
              className="font-body text-[9px] tracking-[0.25em] uppercase flex items-center gap-1.5 transition-opacity duration-200 hover:opacity-60"
              style={{ color: '#C4933A' }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* No results state */}
      {filteredProperties.length === 0 && (
        <div className="px-6 md:px-10 py-16 text-center">
          <p className="font-display text-2xl font-light mb-3" style={{ color: 'rgba(17,24,39,0.35)' }}>No properties match your filters</p>
          <p className="font-body text-xs tracking-[0.15em] mb-6" style={{ color: 'rgba(17,24,39,0.3)' }}>Try adjusting your selection above</p>
          <button
            onClick={() => { setPriceFilter('all'); setBedsFilter('all'); setLocationFilter('all'); }}
            className="font-body text-[10px] tracking-[0.25em] uppercase px-5 py-2.5 transition-all duration-200 hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #A67A28, #C4933A)', color: '#1A0E08', borderRadius: '2px' }}
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Mobile: stacked cards */}
      {filteredProperties.length > 0 && isMobile &&
      <div className="flex flex-col gap-0">
          {filteredProperties.map((property) =>
        <Link
          key={property.slug}
          href={`/properties/${property.slug}`}
          className="group block relative overflow-hidden"
          style={{ height: '70vw', maxHeight: '420px', borderBottom: '1px solid rgba(17,24,39,0.12)' }}>
          
              <img
            src={property.image}
            alt={property.alt}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          
              <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(17,24,39,0.85) 0%, rgba(17,24,39,0.2) 60%, transparent 100%)' }} />
          
              {property.tag &&
          <div className="absolute top-4 left-4">
                  <span
              className="font-body text-[10px] tracking-[0.25em] uppercase px-3 py-1.5"
              style={{ background: 'rgba(196,147,58,0.9)', color: '#1A0E08' }}>
              
                    {property.tag}
                  </span>
                </div>
          }
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="font-body text-[9px] tracking-[0.3em] uppercase mb-1" style={{ color: 'rgba(242,237,230,0.5)' }}>
                  {property.index}
                </p>
                <h3 className="font-display text-2xl font-light mb-1" style={{ color: '#F2EDE6' }}>
                  {property.title}
                </h3>
                <p className="font-body text-xs tracking-[0.15em] uppercase mb-3" style={{ color: 'rgba(242,237,230,0.6)' }}>
                  {property.location}
                </p>
                <div className="flex items-center justify-between">
                  <p className="font-body text-sm font-medium" style={{ color: '#D4A84B' }}>
                    {property.price}
                  </p>
                  <p className="font-body text-xs" style={{ color: 'rgba(242,237,230,0.5)' }}>
                    {[property.beds, property.baths].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
            </Link>
        )}
        </div>}

      {/* Desktop: DDRE-style horizontal accordion */}
      {filteredProperties.length > 0 && !isMobile &&
      <div
        className="flex w-full"
        style={{ height: 'clamp(480px, 65vh, 780px)' }}>
        
          {filteredProperties.map((property, i) => {
          const isActive = activeIndex === i;
          const isCollapsed = activeIndex !== null && !isActive;

          return (
            <div
              key={property.slug}
              className="relative overflow-hidden cursor-pointer"
              style={{
                flex: isActive ? '5 0 0%' : isCollapsed ? '0.3 0 0%' : '1 0 0%',
                transition: 'flex 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                borderRight: i < filteredProperties.length - 1 ? '1px solid rgba(17,24,39,0.15)' : 'none'
              }}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}>
              
                {/* Background image */}
                <img
                src={property.image}
                alt={property.alt}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  transform: isActive ? 'scale(1.04)' : 'scale(1)',
                  transition: 'transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                }} />
              

                {/* Dark gradient overlay — stronger when collapsed */}
                <div
                className="absolute inset-0"
                style={{
                  background: isActive ?
                  'linear-gradient(to top, rgba(17,24,39,0.88) 0%, rgba(17,24,39,0.3) 55%, rgba(17,24,39,0.1) 100%)' :
                  'linear-gradient(to top, rgba(17,24,39,0.75) 0%, rgba(17,24,39,0.55) 100%)',
                  transition: 'background 0.5s ease'
                }} />
              

                {/* Gold shimmer overlay on active */}
                <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(196,147,58,0.06) 0%, transparent 60%)',
                  opacity: isActive ? 1 : 0,
                  transition: 'opacity 0.5s ease'
                }} />
              

                {/* Tag badge */}
                {property.tag &&
              <div
                className="absolute top-5 left-5"
                style={{
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'translateY(0)' : 'translateY(-6px)',
                  transition: 'opacity 0.4s ease 0.2s, transform 0.4s ease 0.2s'
                }}>
                
                    <span
                  className="font-body text-[9px] tracking-[0.3em] uppercase px-3 py-1.5"
                  style={{ background: 'rgba(196,147,58,0.92)', color: '#1A0E08' }}>
                  
                      {property.tag}
                    </span>
                  </div>
              }

                {/* Collapsed state: vertical index label */}
                <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{
                  opacity: isCollapsed ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}>
                
                  <span
                  className="font-display font-light"
                  style={{
                    fontSize: '11px',
                    letterSpacing: '0.3em',
                    color: 'rgba(242,237,230,0.5)',
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    transform: 'rotate(180deg)',
                    textTransform: 'uppercase'
                  }}>
                  
                    {property.index}
                  </span>
                </div>

                {/* Expanded content — bottom */}
                <Link
                href={`/properties/${property.slug}`}
                className="absolute inset-0 flex flex-col justify-end p-7 md:p-9"
                draggable={false}
                style={{
                  opacity: isActive ? 1 : 0,
                  transition: 'opacity 0.4s ease 0.15s',
                  pointerEvents: isActive ? 'auto' : 'none'
                }}>
                
                  {/* Index */}
                  <p
                  className="font-body text-[9px] tracking-[0.4em] uppercase mb-2"
                  style={{ color: 'rgba(196,147,58,0.7)' }}>
                  
                    {property.index}
                  </p>

                  {/* Title */}
                  <h3
                  className="font-display font-light leading-tight mb-1"
                  style={{
                    fontSize: 'clamp(22px, 2.5vw, 38px)',
                    color: '#F2EDE6',
                    transform: isActive ? 'translateY(0)' : 'translateY(12px)',
                    transition: 'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94) 0.1s'
                  }}>
                  
                    {property.title}
                  </h3>

                  {/* Location */}
                  <p
                  className="font-body text-xs tracking-[0.2em] uppercase mb-4"
                  style={{
                    color: 'rgba(242,237,230,0.55)',
                    transform: isActive ? 'translateY(0)' : 'translateY(12px)',
                    transition: 'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94) 0.15s'
                  }}>
                  
                    {property.location}
                  </p>

                  {/* Gold divider */}
                  <div
                  className="mb-4"
                  style={{
                    height: '1px',
                    background: 'linear-gradient(90deg, #C4933A, #D4A84B, transparent)',
                    width: isActive ? '48px' : '0px',
                    transition: 'width 0.5s cubic-bezier(0.25,0.46,0.45,0.94) 0.2s'
                  }} />
                

                  {/* Description */}
                  <p
                  className="font-body text-xs leading-relaxed mb-5 max-w-xs"
                  style={{
                    color: 'rgba(242,237,230,0.6)',
                    transform: isActive ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94) 0.2s'
                  }}>
                  
                    {property.description}
                  </p>

                  {/* Specs row */}
                  <div
                  className="flex items-center gap-4 mb-5"
                  style={{
                    transform: isActive ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94) 0.25s'
                  }}>
                  
                    {[property.beds, property.baths, property.size].filter(Boolean).map((spec) =>
                  <span
                    key={spec}
                    className="font-body text-[10px] tracking-[0.15em] uppercase"
                    style={{ color: 'rgba(242,237,230,0.5)' }}>
                    
                        {spec}
                      </span>
                  )}
                  </div>

                  {/* Price + CTA row */}
                  <div
                  className="flex items-center justify-between"
                  style={{
                    transform: isActive ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94) 0.3s'
                  }}>
                  
                    <p
                    className="font-display font-light"
                    style={{ fontSize: 'clamp(16px, 1.8vw, 24px)', color: '#D4A84B' }}>
                    
                      {property.price}
                    </p>
                    <span
                    className="font-body text-[9px] tracking-[0.3em] uppercase flex items-center gap-2"
                    style={{ color: 'rgba(242,237,230,0.5)' }}>
                    
                      View Property
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </Link>

                {/* Collapsed bottom label (title only, visible when not active and not collapsed) */}
                <div
                className="absolute bottom-0 left-0 right-0 p-5 pointer-events-none"
                style={{
                  opacity: activeIndex === null ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}>
                
                  <p
                  className="font-body text-[9px] tracking-[0.3em] uppercase mb-1"
                  style={{ color: 'rgba(196,147,58,0.6)' }}>
                  
                    {property.index}
                  </p>
                  <h3
                  className="font-display text-lg font-light"
                  style={{ color: '#F2EDE6' }}>
                  
                    {property.title}
                  </h3>
                  <p
                  className="font-body text-[10px] tracking-[0.15em] uppercase mt-0.5"
                  style={{ color: 'rgba(242,237,230,0.45)' }}>
                  
                    {property.location}
                  </p>
                </div>
              </div>);

        })}
        </div>
      }

      {/* Bottom bar */}
      <div
        className="px-6 md:px-10 py-5 flex items-center justify-between"
        style={{ borderTop: '1px solid rgba(17,24,39,0.1)' }}>
        
        <p className="font-body text-xs tracking-[0.2em] uppercase" style={{ color: 'rgba(17,24,39,0.4)' }}>
          {filteredProperties.length} {filteredProperties.length === 1 ? 'Property' : 'Properties'}
          {activeFilterCount > 0 && <span style={{ color: '#C4933A' }}> · Filtered</span>}
        </p>
        <Link
          href="/properties/stockwell-house"
          className="font-body text-xs tracking-[0.2em] uppercase flex items-center gap-2 transition-opacity duration-200 hover:opacity-60"
          style={{ color: '#111827' }}>
          
          View All
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </section>);

}

// ─── FOUNDER SECTION ──────────────────────────────────────────────────────────

function FounderSection() {
  return (
    <section id="founder" style={{ background: '#1A0E08' }}>
      <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
        {/* Image */}
        <div className="relative overflow-hidden h-[60vh] md:h-auto group">
          <img
            src="/assets/images/WhatsApp_Image_2026-07-21_at_17.21.28__3_-1785828576397.jpeg"
            alt="Key Privé KP monogram logo on deep chocolate brown background"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
          
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, transparent 60%, rgba(26,14,8,0.8) 100%)' }} />
          {/* Gold corner */}
          <div className="absolute bottom-8 left-8 pointer-events-none">
            <div className="w-12 h-12">
              <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: 'linear-gradient(90deg, #C4933A, transparent)' }} />
              <div className="absolute bottom-0 left-0 h-full w-px" style={{ background: 'linear-gradient(0deg, #C4933A, transparent)' }} />
            </div>
          </div>
        </div>

        {/* Text */}
        <div className="flex items-center px-8 md:px-16 lg:px-20 py-16 md:py-0"
        style={{ background: 'linear-gradient(135deg, #221208 0%, #1A0E08 100%)' }}>
          <ScrollReveal>
            <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-6" style={{ color: '#C4933A' }}>
              04 — Founder
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-light leading-tight mb-6" style={{ color: '#F2EDE6' }}>
              Every business begins with a belief.
            </h2>
            <div className="kp-gold-divider mb-8" />
            <div className="space-y-5 font-body text-base leading-relaxed max-w-md" style={{ color: 'rgba(242,237,230,0.65)' }}>
              <p>
                After almost a decade advising private clients across London's prime and super-prime market, Sian-Louise Tangney created Key Privé to unite modern technology, cinematic storytelling and the enduring power of human relationships.
              </p>
              <p>
                It is not designed to become the largest brokerage. It is being built to become one of the most meaningful.
              </p>
            </div>
            <div className="mt-10 pt-6" style={{ borderTop: '1px solid rgba(196,147,58,0.2)' }}>
              <p className="font-display text-xl italic" style={{ color: '#D4A84B' }}>Sian-Louise Tangney</p>
              <p className="font-body text-xs tracking-[0.2em] uppercase mt-1" style={{ color: 'rgba(242,237,230,0.4)' }}>
                Founder, Key Privé
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>);

}

// ─── TEAM SECTION ─────────────────────────────────────────────────────────────

function TeamSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section id="team" className="relative py-24 md:py-36 overflow-hidden" style={{ background: '#1A0E08' }}>
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C4933A 0%, transparent 50%), radial-gradient(circle at 80% 50%, #3C1B0D 0%, transparent 50%)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ScrollReveal>
          <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-4" style={{ color: '#C4933A' }}>
            05 — The People
          </p>
          <h2 className="font-display text-4xl md:text-7xl font-light mb-16" style={{ color: '#F2EDE6' }}>
            Private Office Advisors.
          </h2>
        </ScrollReveal>

        <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4">
          {TEAM.map((member, i) =>
          <ScrollReveal key={member.name} delay={i * 150} className="flex-shrink-0">
              <Link
              href={`/team/${member.slug}`}
              className="group relative w-[75vw] md:w-[38vw] lg:w-[28vw] aspect-[4/5] overflow-hidden cursor-pointer block"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}>
              
                <img
                src={member.image}
                alt={member.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-800"
                style={{ transform: hoveredIndex === i ? 'scale(1.06)' : 'scale(1)', transition: 'transform 0.8s cubic-bezier(0.25,0.46,0.45,0.94)', ...(member.slug === 'maxie-jameson' ? { filter: 'grayscale(100%)' } : {}) }} />
              
                <div className="absolute inset-0 transition-opacity duration-500"
              style={{ background: 'linear-gradient(to top, rgba(26,14,8,0.9) 0%, rgba(26,14,8,0.2) 60%, transparent 100%)', opacity: hoveredIndex === i ? 1 : 0.7 }} />

                {/* Gold border on hover */}
                <div className="absolute inset-0 transition-opacity duration-500 pointer-events-none"
              style={{ border: '1px solid rgba(196,147,58,0.4)', opacity: hoveredIndex === i ? 1 : 0 }} />

                <div className="absolute bottom-0 left-0 p-5 md:p-8">
                  <h3 className="font-display text-2xl md:text-3xl font-light transition-colors duration-300"
                style={{ color: hoveredIndex === i ? '#D4A84B' : '#F2EDE6' }}>
                    {member.name}
                  </h3>
                  <div className="h-px w-0 mt-2 mb-2 transition-all duration-500"
                style={{ background: 'linear-gradient(90deg, #C4933A, transparent)', width: hoveredIndex === i ? '48px' : '0px' }} />
                  <p className="font-body text-xs tracking-[0.15em] uppercase" style={{ color: 'rgba(242,237,230,0.5)' }}>
                    {member.role}
                  </p>
                  {/* View bio hint */}
                  <p
                  className="font-body text-[9px] tracking-[0.25em] uppercase mt-2 transition-all duration-500"
                  style={{ color: '#C4933A', opacity: hoveredIndex === i ? 1 : 0, transform: hoveredIndex === i ? 'translateY(0)' : 'translateY(6px)' }}>
                    View Profile →
                  </p>
                </div>
              </Link>
            </ScrollReveal>
          )}
        </div>
      </div>
    </section>);

}

// ─── PRESS SECTION ────────────────────────────────────────────────────────────

function PressSection() {
  return (
    <section id="press" className="relative py-32 md:py-48 overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #2A1208 0%, #3C1B0D 50%, #2A1208 100%)' }}>
      {/* Background image */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://media.base44.com/images/public/6a59f2868433765f69dbba18/7aad3082a_generated_image.png/v1/fill/w_1280,h_1280,al_c,q_90,usm_0.66_1.00_0.01,enc_webp,quality_auto/7aad3082a_generated_image.webp"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover" />
        
      </div>

      {/* Gold radial glow */}
      <div className="absolute inset-0 pointer-events-none"
      style={{ background: 'radial-gradient(ellipse at center, rgba(196,147,58,0.08) 0%, transparent 70%)' }} />

      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <ScrollReveal>
          <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-8" style={{ color: '#C4933A' }}>
            06 — In the Press
          </p>
          {/* Large quote mark */}
          <div className="font-display text-8xl font-light mb-4 leading-none" style={{ color: 'rgba(196,147,58,0.3)' }}>
            &ldquo;
          </div>
          <blockquote className="font-display text-3xl md:text-5xl font-light italic leading-tight mb-8" style={{ color: '#F2EDE6' }}>
            Technology will continue to evolve. Trust remains irreplaceable.
          </blockquote>
          <div className="kp-gold-divider--center kp-gold-divider mb-4" />
          <p className="font-body text-sm tracking-[0.25em] uppercase" style={{ color: 'rgba(196,147,58,0.7)' }}>
            PrimeResi
          </p>
        </ScrollReveal>
      </div>
    </section>);

}

// ─── JOIN SECTION ─────────────────────────────────────────────────────────────

function JoinSection() {
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.getElementById(href.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="join" className="relative h-screen flex items-center justify-center overflow-hidden" style={{ background: '#1A0E08' }}>
      {/* Background */}
      <div className="absolute inset-0 opacity-35">
        <img
          src="/assets/images/WhatsApp_Image_2026-07-21_at_17.21.28__2_-1785828576653.jpeg"
          alt="Key Privé KP monogram on rose background"
          className="w-full h-full object-cover animate-ken-burns-reverse" />
        
      </div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(26,14,8,0.75) 0%, rgba(26,14,8,0.5) 50%, rgba(26,14,8,0.85) 100%)' }} />

      {/* Gold radial glow */}
      <div className="absolute inset-0 pointer-events-none"
      style={{ background: 'radial-gradient(ellipse at center, rgba(196,147,58,0.06) 0%, transparent 60%)' }} />

      <div className="relative text-center px-6 max-w-3xl z-10">
        <ScrollReveal>
          <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-6" style={{ color: '#C4933A' }}>
            07 — For Exceptional Brokers
          </p>
          <h2 className="font-display text-4xl md:text-7xl font-light leading-tight mb-6" style={{ color: '#F2EDE6' }}>
            Build a business you&apos;re proud of.
          </h2>
          <div className="kp-gold-divider--center kp-gold-divider mb-12" />
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="kp-btn-outline inline-block">
            
            Request a Private Conversation
          </a>
        </ScrollReveal>
      </div>
    </section>);

}

// ─── CONTACT SECTION ──────────────────────────────────────────────────────────

function ContactSection() {
  return (
    <section id="contact" className="min-h-screen flex items-center py-24 md:py-32"
    style={{ background: 'linear-gradient(180deg, #221208 0%, #1A0E08 100%)' }}>
      <div className="max-w-2xl mx-auto px-6 w-full">
        <ScrollReveal>
          <p className="font-body text-[10px] tracking-[0.35em] uppercase mb-4 text-center" style={{ color: '#C4933A' }}>
            08 — Begin a Conversation
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light mb-4 text-center" style={{ color: '#F2EDE6' }}>
            Tell us where your journey begins.
          </h2>
          <div className="kp-gold-divider--center kp-gold-divider mb-16" />
        </ScrollReveal>
        <ContactForm />
      </div>
    </section>);

}

// ─── FOOTER ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="py-12 px-6 md:px-12" style={{ background: '#1A0E08', borderTop: '1px solid rgba(196,147,58,0.15)' }}>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex flex-col items-center md:items-start">
          <p className="font-display text-xl font-light tracking-[0.15em]" style={{ color: '#F2EDE6' }}>Key Privé</p>
          <p className="font-body text-[9px] tracking-[0.4em] uppercase mt-1" style={{ color: '#C4933A' }}>Private Office</p>
        </div>
        <div className="kp-gold-divider hidden md:block" style={{ width: '1px', height: '32px', margin: '0' }} />
        <p className="font-body text-xs tracking-[0.2em] uppercase" style={{ color: 'rgba(242,237,230,0.35)' }}>London · International</p>
        <p className="font-body text-xs tracking-[0.2em] uppercase" style={{ color: 'rgba(242,237,230,0.25)' }}>© 2026 Key Privé</p>
      </div>
    </footer>);

}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function HomePage() {
  return (
    <div style={{ background: '#1A0E08' }}>
      <ProgressBar />
      <Navbar />
      <HeroSection />
      <PhilosophySection />
      <ServicesSection />
      <PortfolioSection />
      <FounderSection />
      <TeamSection />
      <PressSection />
      <JoinSection />
      <ContactSection />
      <Footer />
    </div>);

}